import { useState, useRef, useEffect } from "react";
import { Shield, Loader2, Copy, Check, Zap, Volume2, VolumeX, ShieldCheck, RefreshCw, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import { toast } from "sonner";
import PaywallModal from "./PaywallModal";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import BehavioralForecastPanel from "./BehavioralForecastPanel";

interface NeutralizerChatProps {
  message: string;
  score: number;
  verdict: string;
  savedResponse?: string | null;
  savedDecoder?: string | null;
  analysisId?: string | null;
  contextMode?: "professional" | "personal" | "public";
  relationshipType?: string | null;
  tactic?: string | null;
  archetype?: string | null;
}

type ReplyMode = "soft_neutral" | "firm_boundary" | "minimal_grey_rock";

interface ReplyVariants {
  soft_neutral: string;
  firm_boundary: string;
  minimal_grey_rock: string;
}

const REPLY_MODE_META: Record<ReplyMode, { label: string; icon: typeof ShieldCheck; colorClass: string; bgClass: string; borderClass: string }> = {
  soft_neutral: {
    label: "Soft Neutral",
    icon: ShieldCheck,
    colorClass: "text-emerald-400",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/25",
  },
  firm_boundary: {
    label: "Firm Boundary",
    icon: RefreshCw,
    colorClass: "text-cyan",
    bgClass: "bg-cyan/10",
    borderClass: "border-cyan/25",
  },
  minimal_grey_rock: {
    label: "Minimal Grey Rock",
    icon: Ban,
    colorClass: "text-amber-400",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/25",
  },
};


async function parseSSEResponse(response: Response): Promise<{
  response?: string;
  decoder?: string;
  reply_variants?: ReplyVariants;
  success?: boolean;
  error?: string;
}> {
  const text = await response.text();

  if (!text.startsWith("data:")) {
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      return { error: "Failed to parse response" };
    }
  }

  const lines = text.split("\n");
  let lastComplete: any = null;

  for (const line of lines) {
    if (line.startsWith("data: ") && !line.includes("[DONE]")) {
      try {
        const payload = JSON.parse(line.slice(6));
        if (payload.type === "complete") {
          lastComplete = payload;
        }
      } catch { /* skip */ }
    }
  }

  if (lastComplete) {
    return {
      response: lastComplete.response || "",
      decoder: lastComplete.decoder || lastComplete.subtext_decoder || "",
      reply_variants: lastComplete.reply_variants || undefined,
      success: lastComplete.success,
    };
  }

  return { error: "No complete event received" };
}

const NeutralizerChat = ({ message, score, verdict, savedResponse, savedDecoder, analysisId, contextMode = "personal", relationshipType, tactic, archetype }: NeutralizerChatProps) => {
  const { credits, refetch } = useShieldCredits();
  const { user } = useAuth();
  const [response, setResponse] = useState(savedResponse || "");
  const [decoder, setDecoder] = useState(savedDecoder || "");
  const [variants, setVariants] = useState<ReplyVariants | null>(null);
  const [activeMode, setActiveMode] = useState<ReplyMode>("soft_neutral");
  const [isGenerating, setIsGenerating] = useState(false);
  const [pulsePhase, setPulsePhase] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const responseCardRef = useRef<HTMLDivElement>(null);

  

  useEffect(() => {
    if (savedResponse && !response) setResponse(savedResponse);
  }, [savedResponse]);

  useEffect(() => {
    if (savedDecoder && !decoder) setDecoder(savedDecoder);
  }, [savedDecoder]);

  const displayedResponse = variants ? variants[activeMode] : response;

  const handleGenerate = async () => {
    if (credits <= 0) {
      setShowPricing(true);
      return;
    }

    setIsGenerating(true);
    setPulsePhase(true);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const fetchResponse = await fetch(`${supabaseUrl}/functions/v1/analyze-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ message, mode: contextMode, relationship_type: relationshipType || null }),
      });

      setPulsePhase(false);

      const parsed = await parseSSEResponse(fetchResponse);

      if (parsed.error && !parsed.response) {
        throw new Error(parsed.error);
      }

      const generatedResponse = parsed.response || "";
      const generatedDecoder = parsed.decoder || "";
      const generatedVariants = parsed.reply_variants || null;

      if (!generatedResponse && !generatedVariants) {
        throw new Error("No response generated");
      }

      setResponse(generatedResponse);
      setDecoder(generatedDecoder);

      if (generatedVariants && generatedVariants.soft_neutral) {
        setVariants(generatedVariants);
        setActiveMode("soft_neutral");
      }

      refetch();
      toast.success("Response generated!");

      setTimeout(() => {
        responseCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (err) {
      console.error("Neutralize error:", err);
      toast.error("Failed to generate response. Please try again.");
    } finally {
      setIsGenerating(false);
      setPulsePhase(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayedResponse);
      setIsCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleSpeak = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(displayedResponse);
    utterance.pitch = 1.0;
    utterance.rate = 0.9;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const hasResponse = !!displayedResponse;

  return (
    <>
      <div className="w-full rounded-xl border border-border bg-card overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-cyan" />
          <h3 className="text-[11px] uppercase font-mono font-semibold tracking-[0.16em] text-foreground">
            Suggested Grey Rock Reply
          </h3>
        </div>

        <div className="p-4 space-y-3">
          {/* "Them" bubble */}
          <div className="flex justify-start">
            <div className="max-w-[85%]">
              <p className="text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono mb-1">
                Them
              </p>
              <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-secondary border border-border">
                <p className="text-[13px] text-foreground/80 italic leading-snug">{message}</p>
              </div>
            </div>
          </div>

          {/* Mode segmented selector — always visible, integrated */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono shrink-0">
              Mode
            </span>
            <div className="inline-flex p-0.5 rounded-lg bg-background/60 border border-border/80 shadow-inner">
              {(Object.keys(REPLY_MODE_META) as ReplyMode[]).map((mode) => {
                const meta = REPLY_MODE_META[mode];
                const Icon = meta.icon;
                const isActive = activeMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => {
                      setActiveMode(mode);
                      setIsCopied(false);
                      if (isSpeaking) {
                        window.speechSynthesis.cancel();
                        setIsSpeaking(false);
                      }
                    }}
                    className={cn(
                      "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all duration-200",
                      isActive
                        ? cn(meta.bgClass, meta.colorClass, "shadow-[inset_0_0_0_1px_currentColor] font-semibold")
                        : "text-muted-foreground/55 hover:text-foreground/80"
                    )}
                  >
                    <Icon className="w-2.5 h-2.5" /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Behavioral Forecast — updates live with response mode */}
          <BehavioralForecastPanel
            tactic={tactic}
            archetype={archetype}
            responseMode={activeMode}
          />

          {/* Response area */}

          {!hasResponse ? (
            <div className="flex justify-end">
              <div className="max-w-[85%] flex flex-col items-end">
                {isGenerating ? (
                  <div
                    className={cn(
                      "px-4 py-2.5 rounded-2xl rounded-br-sm border border-cyan/30 bg-cyan/5",
                      pulsePhase && "animate-pulse"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan" />
                      <p className="text-[12px] text-cyan font-mono">
                        {pulsePhase ? "AI is neutralizing..." : "Finalizing response..."}
                      </p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleGenerate}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-2xl rounded-br-sm font-semibold text-[13px]",
                      "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                      "hover:scale-[1.02] hover:shadow-[0_0_28px_-4px_hsl(184_100%_50%/0.6)] transition-all",
                      "shadow-[0_0_20px_-4px_hsl(184_100%_50%_/_0.4)]"
                    )}
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Write a Reply — 1 credit
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Decoder */}
              {decoder && (
                <div className="px-3 py-2 rounded-lg bg-cyan/5 border border-cyan/20">
                  <p className="text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono mb-0.5">
                    The Decoder
                  </p>
                  <p className="text-[12.5px] text-cyan italic leading-snug">"{decoder}"</p>
                </div>
              )}

              {/* Response card */}
              <div ref={responseCardRef} className="flex justify-end">
                <div className="w-full relative rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.06] via-background to-cyan/[0.04] overflow-hidden shadow-[0_0_24px_-8px_hsl(160_84%_45%/0.25)]">
                  <div className="px-3 py-2 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 font-mono">
                        Reply Ready · {REPLY_MODE_META[activeMode].label}
                      </span>
                    </div>
                  </div>

                  {/* Response text */}
                  <div className="mx-3 my-2.5 px-3 py-2.5 rounded-lg bg-card border border-emerald-500/20">
                    <p className="text-[13px] text-foreground leading-snug">{displayedResponse}</p>
                  </div>

                  {/* In-card action tools */}
                  <div className="px-3 pb-2.5 flex items-center gap-2 justify-end">
                    <button
                      onClick={handleCopy}
                      className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all",
                        isCopied
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                          : "bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground border border-border"
                      )}
                    >
                      {isCopied ? (
                        <><Check className="w-3 h-3" /> Copied</>
                      ) : (
                        <><Copy className="w-3 h-3" /> Copy</>
                      )}
                    </button>
                    <button
                      onClick={handleSpeak}
                      className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all",
                        isSpeaking
                          ? "bg-cyan/20 text-cyan border border-cyan/40"
                          : "bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground border border-border"
                      )}
                    >
                      {isSpeaking ? (
                        <><VolumeX className="w-3 h-3" /> Stop</>
                      ) : (
                        <><Volume2 className="w-3 h-3" /> Speak</>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Saved confirmation */}
              <Link
                to="/reports"
                className="text-[10.5px] text-emerald-400/80 font-mono text-right flex items-center justify-end gap-1.5 hover:text-emerald-400 transition-colors"
              >
                <ShieldCheck className="w-2.5 h-2.5" /> Saved to Reports →
              </Link>
            </>
          )}
        </div>
      </div>

      <PaywallModal open={showPricing} onOpenChange={setShowPricing} />
    </>
  );
};

export default NeutralizerChat;